import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentMethod, CreateDepositRequest, useGetExchangeRatePublicQuery, useGetRechargeReceptionAddressesQuery } from '../../store/api/balanceApi';
import { ButtonSpinner } from '../ui/Loader';

interface RechargeFormProps {
  paymentMethod: PaymentMethod;
  onSubmit: (data: CreateDepositRequest) => Promise<void>;
  isLoading?: boolean;
}

// Currency options for each payment method
const getAvailableCurrencies = (method: PaymentMethod): ('USD' | 'EUR' | 'SYP')[] => {
  switch (method) {
    case 'payeer':
      return ['USD'];
    case 'crypto':
      return ['USD'];
    case 'syriatel':
      return ['SYP'];
    case 'sham-cash':
      return ['SYP', 'USD'];
    default:
      return ['USD'];
  }
};


export function RechargeForm({ paymentMethod, onSubmit, isLoading = false }: RechargeFormProps) {
  const { t } = useTranslation();
  
  // Fetch exchange rate from public API: GET /api/v1/exchange-rate
  const { data: exchangeRateResponse } = useGetExchangeRatePublicQuery();
  // Fetch minAmount from backend config
  const { data: receptionAddressesResponse } = useGetRechargeReceptionAddressesQuery();

  // Memoize available currencies to prevent unnecessary re-renders
  const availableCurrencies = useMemo(
    () => getAvailableCurrencies(paymentMethod),
    [paymentMethod]
  );
  
  const defaultCurrency = useMemo(
    () => availableCurrencies[0],
    [availableCurrencies]
  );

  // Get minAmount from backend config based on payment method
  const minAmount = useMemo(() => {
    const receptionData = receptionAddressesResponse?.data;
    if (!receptionData) return 0.1;
    switch (paymentMethod) {
      case 'payeer': return receptionData.payeer?.minAmount ?? 0.1;
      case 'crypto': return receptionData.crypto?.minAmount ?? 0.1;
      case 'syriatel': return receptionData.syriatelCash?.minAmount ?? 0.1;
      case 'sham-cash': return receptionData.shamCash?.minAmount ?? 0.1;
      default: return 0.1;
    }
  }, [paymentMethod, receptionAddressesResponse]);

  // Get exchange rate with safe fallback
  // API Response: { status: "success", data: { usdToSypExchangeRate: number, fromCurrency: "USD", toCurrency: "SYP" } }
  const exchangeRate = useMemo(() => {
    if (exchangeRateResponse?.data?.usdToSypExchangeRate) {
      const rate = exchangeRateResponse.data.usdToSypExchangeRate;
      if (typeof rate === 'number' && rate > 0) {
        return rate;
      }
    }
    // Default fallback if API fails or returns invalid data
    return 10000;
  }, [exchangeRateResponse]);

  const [formData, setFormData] = useState<CreateDepositRequest>({
    amount: 0,
    currency: defaultCurrency,
  });

  const [amountInput, setAmountInput] = useState<string>('');

  // Update currency when payment method changes
  useEffect(() => {
    setFormData((prev) => {
      const isCurrencyAvailable = availableCurrencies.includes(prev.currency as 'USD' | 'EUR' | 'SYP');
      if (isCurrencyAvailable) {
        // Currency is still valid, no need to update
        return prev;
      }
      // Currency is not available, update to default
      return {
        ...prev,
        currency: defaultCurrency,
      };
    });
  }, [paymentMethod, defaultCurrency, availableCurrencies]);

  const [errors, setErrors] = useState<{ amount?: string }>({});
  const [touched, setTouched] = useState({ amount: false });

  const validateAmount = (amount: number): string | undefined => {
    // السماح بإدخال 0 أثناء الكتابة (مثلاً 0.5)
    if (amountInput === '' || amountInput === '0' || amountInput === '.') {
      return t('recharge.validation.amountRequired');
    }
    if (amount <= 0) {
      return t('recharge.validation.amountRequired');
    }
    if (minAmount > 0 && amount < minAmount && formData.currency === 'USD') {
      return t('recharge.validation.amountMin');
    }
    if (minAmount > 0 && formData.currency === 'SYP') {
      const minSyp = minAmount * exchangeRate;
      if (amount < minSyp) {
        return t('recharge.validation.amountMinSYP');
      }
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ amount: true });

    const amountError = validateAmount(formData.amount);
    if (amountError) {
      setErrors({ amount: amountError });
      return;
    }

    setErrors({});
    await onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      // السماح بإدخال الأرقام والنقطة العشرية فقط
      const numericValue = value.replace(/[^0-9.]/g, '');
      
      // السماح بنقطة عشرية واحدة فقط
      const parts = numericValue.split('.');
      const sanitizedValue = parts.length > 2 
        ? parts[0] + '.' + parts.slice(1).join('')
        : numericValue;
      
      setAmountInput(sanitizedValue);
      
      // تحويل إلى رقم للتحقق
      const numValue = sanitizedValue === '' || sanitizedValue === '.' 
        ? 0 
        : Number.parseFloat(sanitizedValue) || 0;
      
      setFormData((prev) => ({
        ...prev,
        amount: numValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleBlur = (field: 'amount') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    if (field === 'amount') {
      const error = validateAmount(formData.amount);
      setErrors((prev) => ({ ...prev, amount: error }));
    }
  };

  const getPaymentMethodLabel = (): string => {
    const labels: Record<PaymentMethod, string> = {
      payeer: t('recharge.methods.payeer'),
      crypto: t('recharge.methods.crypto'),
      syriatel: t('recharge.methods.syriatel'),
      'sham-cash': t('recharge.methods.shamCash'),
    };
    return labels[paymentMethod] || paymentMethod;
  };

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6" noValidate>
      <div className="bg-neutral-dark/30 rounded-lg p-4 mb-4">
        <p className="text-sm text-neutral-medium font-body">
          {t('recharge.selectedMethod')}: <span className="text-brand-accent font-semibold">{getPaymentMethodLabel()}</span>
        </p>
      </div>

      {/* Amount */}
      <div>
        <label
          htmlFor="amount"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('recharge.amount')} <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="amount"
          name="amount"
          inputMode="decimal"
          value={amountInput}
          onChange={handleChange}
          onBlur={() => handleBlur('amount')}
          className={`w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            errors.amount && touched.amount
              ? 'border-danger'
              : 'border-neutral-dark'
          }`}
          placeholder={t('recharge.amountPlaceholder')}
        />
        {errors.amount && touched.amount && (
          <p className="mt-1 text-sm text-danger font-body animate-slide-down">
            {errors.amount}
          </p>
        )}
        
        {/* Exchange Rate Info for SYP */}
        {formData.currency === 'SYP' && (
          <div className="mt-2 space-y-1">
            <p className="text-xs text-neutral-medium font-body">
              {t('recharge.exchangeRate.info', { rate: exchangeRate.toLocaleString() })}
            </p>
            {amountInput && formData.amount > 0 && (
              <p className="text-xs text-brand-accent font-body">
                {t('recharge.exchangeRate.equivalentUSD', {
                  amount: (formData.amount / exchangeRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                })}
              </p>
            )}
            {minAmount > 0 ? (
              <p className="text-xs text-neutral-medium font-body">
                {t('recharge.minAmount')}: ({t('recharge.exchangeRate.equivalent', {
                  amount: minAmount * exchangeRate,
                  currency: 'SYP'
                })})
              </p>
            ) : (
              <p className="text-xs text-neutral-medium font-body">
                {t('recharge.noMinAmount')}
              </p>
            )}
          </div>
        )}
        
        {/* Min Amount for non-SYP currencies */}
        {formData.currency !== 'SYP' && (
          <p className="mt-1 text-xs text-neutral-medium font-body">
            {minAmount > 0 ? `${t('recharge.minAmount')}: ${minAmount} ${formData.currency}` : t('recharge.noMinAmount')}
          </p>
        )}
      </div>

      {/* Currency */}
      <div>
        <label
          htmlFor="currency"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('recharge.currency')}
        </label>
        {/* Currency Icons - Clickable Buttons Only */}
        <div className="flex gap-3">
          {availableCurrencies.map((currency) => (
            <button
              key={currency}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, currency }));
              }}
              className={`flex items-center justify-center px-4 py-3 rounded-lg transition-all duration-300 cursor-pointer font-body ${
                formData.currency === currency
                  ? 'bg-brand-accent/20 border-2 border-brand-accent scale-105 text-brand-accent'
                  : 'bg-neutral-dark/30 border-2 border-neutral-dark hover:bg-neutral-dark/50 text-neutral-light'
              }`}
            >
              <span className="text-sm font-semibold">{currency}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <ButtonSpinner />
            <span>{t('common.loading')}</span>
          </>
        ) : (
          t('recharge.createInvoice')
        )}
      </button>
    </form>
  );
}

